import React, { useState } from "react";
import ReactMarkdown from "react-markdown";

import styled from "styled-components";

import Button from "components/atoms/Button";
import Text from "components/atoms/text/Text";

import {
    ErrorText,
    HtmlReportBox,
    InputWrap,
    IntroDescription,
    LogAccordionBody,
    LogAccordionCard,
    LogAccordionChevron,
    LogAccordionHeader,
    LogAccordionSummary,
    LogAccordionTitle,
    LogItem,
    LogMessage,
    LogMeta,
    LogStage,
    PanelCard,
    PhaseBadge,
    ReportBox,
    SourceBadge,
    SourceItem,
    SourceList,
    SourceMetaRow,
    StatusRow,
} from "components/organisms/report/styled";

import remarkGfm from "remark-gfm";

const SectionTitle = styled(Text).attrs({
    variant: "body-md-400",
})``;

export default function ResultStepSection({
    phase,
    error,
    resultState,
    sourceLabelMap,
    onGoBackPlanning,
    onCancelSession,
    onOpenResearch,
    onSaveResearch,
}) {
    const [isLogExpanded, setIsLogExpanded] = useState(false);
    const phaseKind = error ? "error" : phase === "completed" ? "done" : "progress";
    const phaseLabel = error ? "오류" : phase;
    const hasReportContent = !!(resultState.report || "").trim();
    const canOpenResearch = !!resultState.isPersisted && !!resultState.researchNo;
    const canSaveResearch =
        phase === "completed" && !error && hasReportContent && !canOpenResearch && !resultState.isSaving;
    const primaryButtonText = canOpenResearch
        ? "연구노트로 이동"
        : resultState.isSaving
        ? "저장 중..."
        : "연구노트로 저장";
    const logs = resultState.logs || [];
    const logSummary = logs.length > 0 ? `총 ${logs.length}개 로그` : "로그 없음";

    return (
        <PanelCard>
            <StatusRow>
                <SectionTitle>결과 단계</SectionTitle>
                <PhaseBadge kind={phaseKind}>{phaseLabel}</PhaseBadge>
            </StatusRow>

            <IntroDescription>에이전트가 검색 계획에 따라 보고서를 작성합니다.</IntroDescription>

            {error && <ErrorText>{error}</ErrorText>}

            <LogAccordionCard>
                <LogAccordionHeader
                    type="button"
                    onClick={() => setIsLogExpanded((prev) => !prev)}
                    aria-expanded={isLogExpanded}
                    aria-controls="research-log-body"
                >
                    <div>
                        <LogAccordionTitle>검색 수행 로그</LogAccordionTitle>
                        <LogAccordionSummary>{logSummary}</LogAccordionSummary>
                    </div>
                    <LogAccordionChevron expanded={isLogExpanded}>⌄</LogAccordionChevron>
                </LogAccordionHeader>
                {isLogExpanded && (
                    <LogAccordionBody id="research-log-body">
                        {logs.length > 0 && (
                            <>
                                {logs.map((log, index) => (
                                    <LogItem key={`${log.stage}-${index}`}>
                                        <LogStage>{log.stage || "log"}</LogStage>
                                        <LogMessage>{log.message || "-"}</LogMessage>
                                        {log.meta && Object.keys(log.meta).length > 0 && (
                                            <LogMeta>{JSON.stringify(log.meta, null, 2)}</LogMeta>
                                        )}
                                    </LogItem>
                                ))}
                            </>
                        )}
                    </LogAccordionBody>
                )}
            </LogAccordionCard>

            {hasReportContent ? (
                <HtmlReportBox>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{resultState.report}</ReactMarkdown>
                </HtmlReportBox>
            ) : (
                <ReportBox>{resultState.streaming ? "리서치를 진행 중입니다..." : "결과가 아직 없습니다."}</ReportBox>
            )}

            {resultState.sources.length > 0 && (
                <SourceList>
                    <SectionTitle>참고 문헌</SectionTitle>
                    {resultState.sources.map((source) => (
                        <SourceItem
                            key={`${source.index}-${source.url || source.title}`}
                            href={source.url || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <SourceMetaRow>
                                <span>
                                    [{source.index}] {source.title}
                                    {source.year ? ` (${source.year})` : ""}
                                </span>
                                <SourceBadge>{sourceLabelMap[source.source] || source.source || "Unknown"}</SourceBadge>
                            </SourceMetaRow>
                        </SourceItem>
                    ))}
                </SourceList>
            )}

            <InputWrap>
                <Button
                    onlyText
                    width={"140px"}
                    height={"44px"}
                    size={"small"}
                    bgColor={"var(--color-Button1)"}
                    hoverBgColor={"var(--color-ButtonHover1)"}
                    fontColor={"var(--color-White)"}
                    buttonText={primaryButtonText}
                    onClick={canOpenResearch ? onOpenResearch : onSaveResearch}
                    disabled={!canOpenResearch && !canSaveResearch}
                />
                <Button
                    onlyText
                    width={"140px"}
                    height={"44px"}
                    size={"small"}
                    bgColor={"var(--color-Button3)"}
                    hoverBgColor={"var(--color-ButtonHover3)"}
                    fontColor={"var(--color-White)"}
                    buttonText={"이전 단계"}
                    margin={"0 0 0 auto"}
                    onClick={onGoBackPlanning}
                />
                <Button
                    onlyText
                    width={"140px"}
                    height={"44px"}
                    size={"small"}
                    bgColor={"var(--color-Button3)"}
                    hoverBgColor={"var(--color-ButtonHover3)"}
                    fontColor={"var(--color-White)"}
                    buttonText={"세션 초기화"}
                    onClick={onCancelSession}
                />
            </InputWrap>
        </PanelCard>
    );
}
